import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { UserService } from 'gw-capability-gateway';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Loader, useModal } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { format } from 'date-fns';
import { DateUtil } from '@xengage/gw-portals-util-js';
import TWIABatchService from "ent-capability-policyportfolio/services/TWIABatchService.js";
import metadata from './ReportComponent.metadata.json5';
import styles from './ReportComponent.module.scss';
import messages from '../TWIABatchesLanding/TWIABatchesLanding.messages';

function ReportComponent() {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const [availableProducerCode, setAvailableProducerCode] = useState([]);
    const [reportVM, updateReportVM] = useState({});
    const [producerVal, setProducerVal] = useState([]);
    const [reportTableData, setReportTableData] = useState([]);
    const [showReportTable, setShowReportTable] = useState(false);
    const [updatedAvailCodes, setUpdatedAvailCodes] = useState([]);
    const { showAlert } = useModal();
    const authUserData = _.get(useAuthentication(), 'authUserData');
    const isTWIA = authUserData?.associationName_TWIA === 'twia';
    const pageConfig = {
        page: 0,
        pageSize: 1000
    };

    const getRptAvailableProducerCodes = useCallback(async () => {
        const availableProducerCodes = [];

        await UserService.getAvailableProducerCodesForCurrentUser(authHeader)
            .then(codes => {
                if (codes) {
                    codes.forEach((code) => availableProducerCodes.push(code));
                }
            });
        setUpdatedAvailCodes(availableProducerCodes);

        const res = availableProducerCodes.map(co => {
            const availableProducerCodesforUser = {
                code: co.code,
                name: co.displayValue,
                ...co
            };

            return availableProducerCodesforUser;
        });

        setAvailableProducerCode(res);

        return availableProducerCodes;
    }, [authHeader]);

    useEffect(() => {
        const rptCriteria = viewModelService.create({}, 'pc', 'edge.capabilities.gateway.bulkproducerchange.dto.TWIABBReportCriteriaDTO');

        updateReportVM(rptCriteria);
        getRptAvailableProducerCodes();
    }, [getRptAvailableProducerCodes, viewModelService]);

    const processReport = useCallback((reports) => {

        const reportMap = reports.map(report => ({
            policyNumber: report.policyNumber || '',
            accountNumber: report.accountNumber || '',
            primaryInsuredName: report.primaryInsuredName || '',
            propertyLocation_TWIA: report.propertyLocation_TWIA || '',
            policyEffectiveDate: report.policyEffectiveDate ? format(new Date(report.policyEffectiveDate), 'MM/dd/yyyy') : '',
            expirationDate_TWIA: report.expirationDate_TWIA ? format(new Date(report.expirationDate_TWIA), 'MM/dd/yyyy') : '',
            annualPremium: report.annualPremium?.amount ? `$${report.annualPremium.amount}` : '$0',
            depopulationCarrier_TWIA: report.depopulationCarrier_TWIA || '',
            depopulationYear_TWIA: report.depopulationYear_TWIA || ''
        }));

        setReportTableData(reportMap);

    }, []);

    const loadReports = useCallback(async (queryOptions) => {
        const payload = _.get(reportVM, 'value');

        setShowReportTable(true);

        const batchReportsPromise = await TWIABatchService.generateBookOfBusinessReport(payload, queryOptions , authHeader);

        processReport(batchReportsPromise.items);

        return batchReportsPromise;
    }, [reportVM, authHeader, processReport]);


    const downloadReport = () => {
        const payload = _.get(reportVM, 'value');
        const promise = TWIABatchService.generateBooksOfBusinessReportCSV(payload, authHeader);

        promise.then((resp) => {
            const fileNameParams = payload;
            const outputMediaType = 'text/csv';
            const date = DateUtil.currentDateAtMidnight();

            const formattedDate = date.toISOString().split('T')[0];

            const time = date.toISOString().split('T')[1].slice(0, 5).replace(":", "");

            const formattedDateTime = `${formattedDate}_${time}`;
            const fileNameBasedOnProduct = isTWIA ? 'TWIA_Active_Policies_Report' : 'TFPA_Active_Policies_Report';
            const translatedString = `${fileNameBasedOnProduct}_${fileNameParams.producerCode.code}_${formattedDateTime}`;
            const downloadFileName = translator(translatedString);

            const blob = new Blob([resp], { type: outputMediaType });

            if (window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveBlob(blob, downloadFileName);
            } else {
                const a = window.document.createElement('a');

                a.href = window.URL.createObjectURL(blob);
                a.download = downloadFileName;

                a.click();
            }
        }).catch(() => {
            showAlert({
                title: 'Error',
                message: translator(messages.errorReport),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: translator(messages.close)
            })
        });
    };


    const writeValue = useCallback((value, path) => {
        if (path === 'producerCode') {
            setProducerVal(value);

            const selectedProducer = updatedAvailCodes.find(item => item.code === value);

            if (selectedProducer) {
                _.set(reportVM, path, selectedProducer);
                updateReportVM(reportVM);

            }
        }
    }, [reportVM, updatedAvailCodes]);


    const generateReport = useCallback(() => {
        const { page, pageSize } = pageConfig;
        const startIndex = pageSize * page;
        const offsetEndMinusOne = startIndex + pageSize - 1;
        const queryOptions = {
            offsetStart: startIndex,
            offsetEnd: offsetEndMinusOne
        }

        _.set(reportVM, 'newReportRequest', true);
        updateReportVM(reportVM);
        loadReports(queryOptions);
    }, [loadReports, pageConfig, reportVM]);

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        producerCodeDropDown: {
            availableValues: availableProducerCode,
            value: producerVal
        },
        generateReportButton: {
            disabled: _.isEmpty(producerVal),
            onClick: generateReport
        },
        exportButton: {
            disabled: _.isEmpty(reportTableData),
            onClick: downloadReport
        },
        reportTableWrapper: {
            visible: showReportTable
        },
        reportTableGrid: {
            data: reportTableData
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateReport,
            downloadReport
        }
    }

    return (
        <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={reportVM}
                overrideProps={overrideProps}
                onModelChange={updateReportVM}
                onValueChange={writeValue}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </Loader>
    );
}

export default ReportComponent;